import { Button } from '@agentisai/design-system';
import type { ParsedOption } from '../lib/parseOptions';
import { optionRow } from './composerChrome.css';

/**
 * A one-click reply that is not a numbered option — e.g. "Continue with
 * Engineer" after a handoff (D14-10). `send` is the user turn it posts.
 */
export interface ActionChip {
  label: string;
  send: string;
}

export type ChipOption = ParsedOption | ActionChip;

export interface OptionChipsProps {
  options: readonly ChipOption[];
  /**
   * Send the pick as an ordinary user turn — the number AND the label, so the
   * transcript reads on scrollback and the agent gets the same thing a typed
   * "2" would have meant. Visibility (latest agent turn, nothing in flight,
   * empty composer) is the parent's call, as with the confirm-design chip.
   */
  onPick: (text: string) => void;
}

/**
 * OptionChips — the agent offered numbered options; click one instead of typing
 * its number. Composer chrome, not a chat turn (same pattern as the green
 * "Looks right — set it up" chip). Renders nothing for an empty list.
 */
export function OptionChips({ options, onPick }: OptionChipsProps) {
  if (options.length === 0) return null;
  return (
    <div role="group" aria-label="Suggested replies" className={optionRow}>
      {options.map((o) => {
        const text = 'n' in o ? `${o.n}. ${o.label}` : o.label;
        return (
          <Button
            key={text}
            size="sm"
            variant="softGreen"
            onClick={() => onPick('send' in o ? o.send : text)}
          >
            {text}
          </Button>
        );
      })}
    </div>
  );
}
OptionChips.displayName = 'OptionChips';
